import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Calendar, Plus, Check, Loader2, ChevronsUpDown, Building2, Mail } from 'lucide-react';

const AddManualQuoteOption = ({ open, onClose, bookingId, courseName, onSuccess }) => {
  const { userProfile, company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingTrainers, setFetchingTrainers] = useState(false);
  const [companyTrainers, setCompanyTrainers] = useState([]);
  const [isAddingProvider, setIsAddingProvider] = useState(false);
  const [providerComboboxOpen, setProviderComboboxOpen] = useState(false);

  const [newQuoteForm, setNewQuoteForm] = useState({
    training_provider_id: '',
    course_name: courseName || '',
    date: null,
    delivery_method: 'onsite',
    location: '',
    duration: '',
    quote_value: '',
    pricing_model: 'per_person'
  });

  const [newProviderForm, setNewProviderForm] = useState({
    name: '',
    email: ''
  });

  // Fetch company trainers when modal opens
  useEffect(() => {
    if (open && company?.id) {
      fetchCompanyTrainers();
    }
  }, [open, company?.id]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      resetForms();
    }
  }, [open]);

  // Update course name when prop changes
  useEffect(() => {
    setNewQuoteForm(prev => ({ ...prev, course_name: courseName || '' }));
  }, [courseName]);

  const fetchCompanyTrainers = async () => {
    if (!company?.id) return;

    try {
      setFetchingTrainers(true);
      const { data, error } = await supabase
        .from('trainers')
        .select('*')
        .eq('company_id', company.id)
        .order('trainer_name');

      if (error) throw error;

      setCompanyTrainers(data || []);
    } catch (error) {
      console.error('Error fetching company trainers:', error);
      toast.error('Failed to load training providers');
    } finally {
      setFetchingTrainers(false);
    }
  };

  const resetForms = () => {
    setNewQuoteForm({
      training_provider_id: '',
      course_name: courseName || '',
      date: null,
      delivery_method: 'onsite',
      location: '',
      duration: '',
      quote_value: '',
      pricing_model: 'per_person'
    });
    setNewProviderForm({
      name: '',
      email: ''
    });
    setIsAddingProvider(false);
    setProviderComboboxOpen(false);
  };

  const handleAddNewProvider = async () => {
    if (!newProviderForm.name.trim()) {
      toast.error('Please enter provider name');
      return;
    }
    if (!newProviderForm.email.trim()) {
      toast.error('Please enter provider email');
      return;
    }

    try {
      const { data: newTrainer, error } = await supabase
        .from('trainers')
        .insert({
          trainer_name: newProviderForm.name.trim(),
          booking_email: newProviderForm.email.trim(),
          company_id: company?.id || null,
          created_by: userProfile.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      setCompanyTrainers(prev => [...prev, newTrainer]);
      setNewQuoteForm(prev => ({ ...prev, training_provider_id: newTrainer.id }));
      setNewProviderForm({ name: '', email: '' });
      setIsAddingProvider(false);
      setProviderComboboxOpen(false);
      toast.success('Training provider added');
    } catch (error) {
      console.error('Error adding provider:', error);
      toast.error('Failed to add provider');
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!newQuoteForm.training_provider_id) {
      toast.error('Please select a training provider');
      return;
    }
    if (!newQuoteForm.date) {
      toast.error('Please select a date');
      return;
    }
    // Location only required for onsite/classroom delivery
    const requiresLocation = newQuoteForm.delivery_method !== 'remote' && newQuoteForm.delivery_method !== 'elearning';
    if (requiresLocation && !newQuoteForm.location.trim()) {
      toast.error('Please enter a location');
      return;
    }
    if (!newQuoteForm.quote_value) {
      toast.error('Please enter a quote amount');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from('quote_options')
        .insert({
          training_quote_id: bookingId,
          training_provider_id: newQuoteForm.training_provider_id,
          course_name: newQuoteForm.course_name || courseName,
          dates: [{ startDate: format(newQuoteForm.date, 'yyyy-MM-dd') }],
          course_delivery: newQuoteForm.delivery_method,
          onsite_postcode: newQuoteForm.location,
          course_duration: newQuoteForm.duration || null,
          quote_value: parseFloat(newQuoteForm.quote_value),
          pricing_model: newQuoteForm.pricing_model || null
          // created_date has a default value of now(), so we don't need to specify it
        });

      if (error) throw error;

      toast.success('Quote option added successfully');
      resetForms();
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error adding quote option:', error);
      toast.error('Failed to add quote option');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open && !isAddingProvider} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
          <div className="overflow-y-auto max-h-[90vh] p-6">
          <DialogHeader>
            <DialogTitle>Add Manual Quote Option</DialogTitle>
            <DialogDescription>
              Add a quote received from a training provider to compare options.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Training Provider Selection */}
            <div className="space-y-2">
              <Label htmlFor="provider">Training Provider *</Label>
              {fetchingTrainers ? (
                <div className="flex items-center gap-2 p-2 border rounded-md">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-gray-500">Loading providers...</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Popover open={providerComboboxOpen} onOpenChange={setProviderComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={providerComboboxOpen}
                        className="flex-1 justify-between"
                      >
                        {newQuoteForm.training_provider_id
                          ? companyTrainers.find((trainer) => trainer.id === newQuoteForm.training_provider_id)?.trainer_name
                          : "Select provider..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search providers..." />
                        <CommandEmpty>
                          <div className="p-4 text-center">
                            <p className="text-sm text-gray-500 mb-3">No providers found</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setProviderComboboxOpen(false);
                                setIsAddingProvider(true);
                              }}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add New Provider
                            </Button>
                          </div>
                        </CommandEmpty>
                        <CommandGroup className="max-h-[300px] overflow-auto">
                          {companyTrainers.map((trainer) => (
                            <CommandItem
                              key={trainer.id}
                              value={`${trainer.trainer_name} ${trainer.booking_email || ''}`}
                              onSelect={() => {
                                setNewQuoteForm(prev => ({ ...prev, training_provider_id: trainer.id }));
                                setProviderComboboxOpen(false);
                              }}
                            >
                              <div className="flex items-center justify-between w-full">
                                <div className="flex items-start gap-2 flex-1">
                                  <Check
                                    className={cn(
                                      "h-4 w-4 mt-1 shrink-0",
                                      newQuoteForm.training_provider_id === trainer.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                                      <p className="font-medium text-sm truncate">{trainer.trainer_name}</p>
                                    </div>
                                    {trainer.booking_email && (
                                      <div className="flex items-center gap-2 mt-1">
                                        <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                                        <p className="text-xs text-gray-500 truncate">{trainer.booking_email}</p>
                                      </div>
                                    )}
                                    {trainer.phone_number && (
                                      <p className="text-xs text-gray-500 mt-0.5">{trainer.phone_number}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setIsAddingProvider(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Course Name */}
            <div className="space-y-2">
              <Label htmlFor="course_name">Course Name</Label>
              <Input
                id="course_name"
                value={newQuoteForm.course_name}
                onChange={(e) => setNewQuoteForm(prev => ({ ...prev, course_name: e.target.value }))}
                placeholder="Enter course name"
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>Training Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !newQuoteForm.date && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {newQuoteForm.date ? format(newQuoteForm.date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPicker
                    mode="single"
                    selected={newQuoteForm.date}
                    onSelect={(date) => setNewQuoteForm(prev => ({ ...prev, date }))}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex gap-4">
              {/* Delivery Method */}
              <div className="space-y-2 w-full">
                <Label htmlFor="delivery_method">Delivery Method *</Label>
                <Select
                  value={newQuoteForm.delivery_method}
                  onValueChange={(value) => setNewQuoteForm(prev => ({ ...prev, delivery_method: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onsite">Onsite</SelectItem>
                    <SelectItem value="classroom">Classroom</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="elearning">E-Learning</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Duration */}
              <div className="space-y-2 w-full">
                <Label htmlFor="duration">Duration</Label>
                <Input
                  id="duration"
                  value={newQuoteForm.duration}
                  onChange={(e) => setNewQuoteForm(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="e.g. 1 day, 2 days"
                />
              </div>
            </div>

            {/* Location - hidden for remote/elearning */}
            {newQuoteForm.delivery_method !== 'remote' && newQuoteForm.delivery_method !== 'elearning' && (
              <div className="space-y-2">
                <Label htmlFor="location">Location/Postcode *</Label>
                <Input
                  id="location"
                  value={newQuoteForm.location}
                  onChange={(e) => setNewQuoteForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Enter location or postcode"
                />
              </div>
            )}

            <div className="flex gap-4">
              {/* Quote Amount */}
              <div className="space-y-2 w-full">
                <Label htmlFor="quote_value">Quote Amount (£) *</Label>
                <Input
                  id="quote_value"
                  type="number"
                  step="0.01"
                  value={newQuoteForm.quote_value}
                  onChange={(e) => setNewQuoteForm(prev => ({ ...prev, quote_value: e.target.value }))}
                  placeholder="0.00"
                />
              </div>

              {/* Pricing Model */}
              <div className="space-y-2 w-full">
                <Label htmlFor="pricing_model">Pricing Model</Label>
                <Select
                  value={newQuoteForm.pricing_model}
                  onValueChange={(value) => setNewQuoteForm(prev => ({ ...prev, pricing_model: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select pricing model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_person">Per Person</SelectItem>
                    <SelectItem value="per_course">Per Course</SelectItem>
                    <SelectItem value="per_day">Per Day</SelectItem>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Quote Option'
              )}
            </Button>
          </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Provider Dialog */}
      <Dialog open={isAddingProvider} onOpenChange={setIsAddingProvider}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Training Provider</DialogTitle>
            <DialogDescription>
              Add a new training provider to your company's list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="provider_name">Provider Name *</Label>
              <Input
                id="provider_name"
                value={newProviderForm.name}
                onChange={(e) => setNewProviderForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter provider name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="provider_email">Booking Email *</Label>
              <Input
                id="provider_email"
                type="email"
                value={newProviderForm.email}
                onChange={(e) => setNewProviderForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="bookings@provider.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddingProvider(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNewProvider}>
              <Check className="h-4 w-4 mr-2" />
              Add Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddManualQuoteOption;
